import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import Card from '@/components/base/Card';
import Tabs from '@/components/base/Tabs';
import StatusBadge from '@/components/base/StatusBadge';
import EmptyState from '@/components/base/EmptyState';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customers, quotes, projects, receivables, collections, serviceRecords, files, projectName } = useData();
  const customer = customers.find((c) => c.id === id);

  const [tab, setTab] = useState('genel');

  if (!customer) {
    return (
      <div className="animate-fade-in-up">
        <EmptyState icon="ri-user-search-line" title="Müşteri bulunamadı" description="Aradığınız kayıt mevcut değil." />
        <div className="mt-4 text-center">
          <button type="button" onClick={() => navigate('/customers')} className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">
            Müşteri Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  const customerQuotes = quotes.filter((q) => q.customerId === customer.id);
  const customerProjects = projects.filter((p) => p.customerId === customer.id);
  const customerReceivables = receivables.filter((r) => r.customerId === customer.id);
  const customerCollections = collections.filter((c) => c.customerId === customer.id);
  const customerFiles = files.filter((f) => f.relatedType === 'Müşteri' && f.relatedId === customer.id);
  const customerServices = serviceRecords.filter((s) => s.customerId === customer.id);

  const activity = [
    ...customerQuotes.map((q) => ({ date: q.date, text: `${q.number} numaralı teklif oluşturuldu (${q.status})`, icon: 'ri-file-list-3-line' })),
    ...customerProjects.map((p) => ({ date: p.startDate, text: `${p.name} projesi başladı`, icon: 'ri-briefcase-line' })),
    ...customerCollections.map((c) => ({ date: c.date, text: `${formatTL(c.amount)} tahsilat alındı (${c.paymentType})`, icon: 'ri-bank-card-line' })),
  ].sort((a, b) => (a.date < b.date ? 1 : -1));

  const tabs = [
    { key: 'genel', label: 'Genel Bilgi' },
    { key: 'teklifler', label: 'Teklifler' },
    { key: 'projeler', label: 'Projeler' },
    { key: 'finans', label: 'Finans' },
    { key: 'gecmis', label: 'Görüşme Geçmişi' },
    { key: 'dosyalar', label: 'Dosyalar' },
    { key: 'servis', label: 'Servis Kayıtları' },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up">
      <button type="button" onClick={() => navigate('/customers')} className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line" /> Müşteriler
      </button>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 font-heading text-lg font-bold text-primary-700">
              {customer.name.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-foreground-950">{customer.name}</h1>
              <p className="mt-1 text-sm text-foreground-500">{customer.address}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5"><i className="ri-user-3-line" /> {customer.contactPerson}</span>
                <span className="flex items-center gap-1.5"><i className="ri-phone-line" /> {customer.phone}</span>
                <span className="flex items-center gap-1.5"><i className="ri-mail-line" /> {customer.email}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div><p className="text-xs text-foreground-500">Toplam Satış</p><p className="font-heading text-lg font-semibold text-foreground-950">{formatTL(customer.totalSales)}</p></div>
            <div><p className="text-xs text-foreground-500">Alacak</p><p className="font-heading text-lg font-semibold text-accent-700">{customer.receivable > 0 ? formatTL(customer.receivable) : '-'}</p></div>
            <div><p className="text-xs text-foreground-500">Aktif Proje</p><p className="font-heading text-lg font-semibold text-foreground-950">{customer.activeProjects}</p></div>
            <div><p className="text-xs text-foreground-500">Açık Teklif</p><p className="font-heading text-lg font-semibold text-foreground-950">{customer.openQuotes}</p></div>
          </div>
        </div>
      </div>

      <Tabs items={tabs} value={tab} onChange={setTab} />

      {tab === 'genel' && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Firma Bilgileri</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-foreground-500">Sektör</dt><dd className="font-medium text-foreground-900">{customer.sector}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">Şehir</dt><dd className="font-medium text-foreground-900">{customer.city}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">Son Görüşme</dt><dd className="font-medium text-foreground-900">{formatDate(customer.lastContact)}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">Müşteri No</dt><dd className="font-medium text-foreground-900">{customer.id}</dd></div>
            </dl>
            {customer.notes && (
              <div className="mt-5 rounded-md bg-background-100 p-4 text-sm text-foreground-700">
                <p className="font-semibold text-foreground-900">Notlar</p>
                <p className="mt-1">{customer.notes}</p>
              </div>
            )}
          </Card>
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">İletişim</h2>
            <div className="mt-4 space-y-3">
              {[
                { label: 'Yetkili Kişi', value: customer.contactPerson, icon: 'ri-user-3-line' },
                { label: 'Telefon', value: customer.phone, icon: 'ri-phone-line' },
                { label: 'E-posta', value: customer.email, icon: 'ri-mail-line' },
                { label: 'Adres', value: customer.address, icon: 'ri-map-pin-line' },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500"><i className={row.icon} /></span>
                  <div><p className="text-xs text-foreground-500">{row.label}</p><p className="text-sm font-medium text-foreground-900">{row.value}</p></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === 'teklifler' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {customerQuotes.length === 0 ? <EmptyState title="Teklif bulunamadı" /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Teklif No</th><th className="px-4 py-3">Proje</th><th className="px-4 py-3">Tutar</th><th className="px-4 py-3">Geçerlilik</th><th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {customerQuotes.map((q) => (
                    <tr key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} className="cursor-pointer border-b border-background-100 last:border-0 hover:bg-background-100/60">
                      <td className="px-5 py-3 font-semibold text-foreground-900">{q.number}</td>
                      <td className="px-4 py-3 text-foreground-700">{q.projectName}</td>
                      <td className="px-4 py-3 font-semibold text-foreground-900">{formatTL(q.amount)}</td>
                      <td className="px-4 py-3 text-foreground-600">{formatDate(q.validityDate)}</td>
                      <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'projeler' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {customerProjects.length === 0 ? <EmptyState title="Proje bulunamadı" /> : customerProjects.map((p) => (
            <Card key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-foreground-500">{p.id}</p>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{p.name}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{p.category}</p>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-foreground-500">İlerleme</span>
                <span className="font-semibold text-foreground-900">%{p.progress}</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-background-200">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${p.progress}%` }} />
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'finans' && (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="border-b border-background-200 px-5 py-3 text-sm font-semibold text-foreground-900">Alacaklar</div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Proje</th><th className="px-4 py-3 text-right">Toplam</th><th className="px-4 py-3 text-right">Tahsil</th><th className="px-4 py-3 text-right">Kalan</th><th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {customerReceivables.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-6 text-center text-foreground-500">Alacak kaydı yok</td></tr>
                  ) : customerReceivables.map((r) => (
                    <tr key={r.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 font-medium text-foreground-900">{projectName(r.projectId)}</td>
                      <td className="px-4 py-3 text-right text-foreground-700">{formatTL(r.totalSales)}</td>
                      <td className="px-4 py-3 text-right text-foreground-700">{formatTL(r.collected)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-accent-700">{formatTL(r.remaining)}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'gecmis' && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="relative border-l-2 border-background-200 pl-6">
            {activity.map((a, i) => (
              <div key={i} className="relative pb-6 last:pb-0">
                <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full bg-background-50 text-foreground-400 ring-1 ring-background-200">
                  <i className={`${a.icon} text-xs`} />
                </span>
                <p className="text-sm font-medium text-foreground-900">{a.text}</p>
                <p className="text-xs text-foreground-500">{formatDate(a.date)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'dosyalar' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {customerFiles.length === 0 ? <EmptyState title="Dosya bulunamadı" /> : customerFiles.map((f) => (
            <Card key={f.id} className="flex items-center gap-3 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-file-3-line" /></span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground-900">{f.name}</p>
                <p className="text-xs text-foreground-500">{f.size} · {formatDate(f.uploadedDate)}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'servis' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {customerServices.length === 0 ? <EmptyState title="Servis kaydı bulunamadı" /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Talep</th><th className="px-4 py-3">Sorun</th><th className="px-4 py-3">Öncelik</th><th className="px-4 py-3">Planlanan</th><th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {customerServices.map((s) => (
                    <tr key={s.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 text-foreground-600">{formatDate(s.requestDate)}</td>
                      <td className="px-4 py-3 text-foreground-800">{s.issue}</td>
                      <td className="px-4 py-3"><Badge tone={s.priority === 'Kritik' ? 'accent' : 'neutral'}>{s.priority}</Badge></td>
                      <td className="px-4 py-3 text-foreground-600">{formatDate(s.plannedDate)}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Link to={`/projects/${customerProjects[0]?.id}`} className={customerProjects.length ? 'cursor-pointer text-sm font-semibold text-primary-600 hover:text-primary-700' : 'hidden'}>
          Projeyi Görüntüle →
        </Link>
      </div>
    </div>
  );
}